import { useState } from "react"
import { LogIn, LogOut, Menu, ShoppingCart, User } from "lucide-react"
import logoAccess from "../../Bilder_Logos/Sinus_Access_Logo.svg"

interface Props {
  authed: boolean
  name: string
  cartCount: number
  onOpenCart: () => void
  onLogin: () => void
  onLogout: () => void
}

const NAV = [
  { href: "#navigator", label: "Milieu-Navigator" },
  { href: "#infopakete", label: "Infopakete" },
  { href: "#vortraege", label: "Vorträge" },
  { href: "#faq", label: "FAQ" },
]

export function Header({ authed, name, cartCount, onOpenCart, onLogin, onLogout }: Props) {
  const [menuOpen, setMenuOpen] = useState(false)

  return (
    <header className="sticky top-0 z-20 border-b border-line bg-white/95 backdrop-blur">
      <div className="mx-auto flex h-16 w-full max-w-content items-center justify-between gap-4 px-5 md:h-[88px] md:px-8">
        <a href="#top" aria-label="SINUS Access – Startseite" className="shrink-0">
          <img src={logoAccess} alt="SINUS Access" className="h-[22px] w-auto md:h-7" />
        </a>

        <nav className="hidden items-center gap-8 text-sm lg:flex">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              className="border-b-2 border-transparent py-1 transition hover:border-brand-blue"
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex shrink-0 items-center gap-2 md:gap-3">
          <button
            onClick={() => setMenuOpen((v) => !v)}
            className="inline-flex items-center rounded-field bg-surface px-3 py-2 lg:hidden"
            aria-label="Menü"
            aria-expanded={menuOpen}
          >
            <Menu className="h-[18px] w-[18px]" />
          </button>

          {authed ? (
            <>
              <span className="hidden items-center gap-1.5 text-sm text-brand-muted md:inline-flex">
                <User className="h-4 w-4" />
                {name}
              </span>
              <button
                onClick={onLogout}
                className="inline-flex items-center gap-2 rounded-field bg-surface px-3 py-2 text-sm font-medium transition hover:bg-line md:px-4"
              >
                <LogOut className="h-4 w-4" />
                <span className="hidden md:inline">Abmelden</span>
              </button>
            </>
          ) : (
            <button
              onClick={onLogin}
              className="inline-flex items-center gap-2 rounded-field bg-surface px-3 py-2 text-sm font-medium transition hover:bg-line md:px-4"
            >
              <LogIn className="h-4 w-4 md:hidden" />
              <span className="hidden md:inline">Login</span>
            </button>
          )}

          <button
            onClick={onOpenCart}
            className={`relative inline-flex items-center gap-2 rounded-field px-3 py-2 text-sm font-medium transition md:px-4 ${
              cartCount > 0
                ? "bg-brand-blue text-white hover:brightness-105"
                : "bg-surface hover:bg-line"
            }`}
            aria-label={`Warenkorb${cartCount > 0 ? `, ${cartCount} Positionen` : ""}`}
          >
            <ShoppingCart className="h-[18px] w-[18px]" />
            <span className="hidden md:inline">Warenkorb</span>
            {cartCount > 0 && (
              <span className="absolute -right-1.5 -top-1.5 flex h-5 min-w-[20px] items-center justify-center rounded-full border border-white bg-brand-blue px-1 text-[12px] font-semibold leading-none text-white">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {menuOpen && (
        <nav className="border-t border-line bg-white px-5 pb-4 lg:hidden">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setMenuOpen(false)}
              className="block border-b border-line py-3 text-sm last:border-0"
            >
              {n.label}
            </a>
          ))}
        </nav>
      )}
    </header>
  )
}
